'use client';

import { useState } from 'react';

const faqs = [
  {
    q: 'How much does a 5-ton Trane AC cost installed in Texas?',
    a: 'In the Dallas–Fort Worth area, a complete 5-ton Trane installation typically ranges from $4,200 to $7,500+, depending on the model (XR14, XR15, or XR16), ductwork condition, permit requirements, and any code upgrades needed. The XR15 and XR16 may qualify for federal tax credits up to $2,000, which can significantly offset the investment. We provide a free written estimate before any work begins.',
  },
  {
    q: 'Is a 5-ton AC system too large for my home?',
    a: 'In Texas, a 5-ton system is typically appropriate for homes between 2,500 and 3,500 square feet — though the exact right size depends on insulation quality, window area, ceiling height, and sun exposure. An oversized system will short-cycle (turn on and off too frequently), creating humidity problems and premature wear. We perform a full ACCA Manual J load calculation before recommending any system size.',
  },
  {
    q: 'How long does a 5-ton AC installation take?',
    a: 'Most 5-ton Trane installations are completed in a single day — typically 4 to 8 hours. This includes removing the old system, installing the new condenser and evaporator coil, connecting refrigerant lines, electrical work, startup testing, and cleanup. Complex situations involving ductwork modifications or permit inspections may require an additional day.',
  },
  {
    q: 'What SEER2 rating should I choose for my Texas home?',
    a: 'For Texas homes, we generally recommend at minimum the XR15 (15.2 SEER2) because it qualifies for federal tax credits and utility rebates that partially offset the higher upfront cost. If you plan to stay in your home long-term and want maximum comfort with two-stage operation, the XR16 (16.2 SEER2) pays for itself in energy savings. The XR14 (14.3 SEER2) is a solid budget option for homeowners planning to sell soon.',
  },
  {
    q: 'Do I need new ductwork when replacing my AC system?',
    a: "Not always. We inspect your existing ductwork as part of the free in-home assessment. If your ducts are properly sized, sealed, and in good condition, they can typically be reused. If we find leaks, undersized runs, or damage, we'll include ductwork repairs or replacement in your written quote — never as a surprise add-on.",
  },
  {
    q: 'What refrigerant does the new Trane 5-ton system use?',
    a: 'New Trane systems are transitioning from R-410A to R-454B, a more environmentally friendly refrigerant with a significantly lower global warming potential. R-410A is being phased out under EPA regulations. All new 5-ton Trane XR series units we install use the newer R-454B refrigerant, which also improves efficiency ratings.',
  },
];

export default function FaqSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section id="faq" className="py-20 md:py-28 bg-white">
      <div className="max-w-4xl mx-auto px-5 md:px-8">
        {/* Header */}
        <div className="text-center mb-14">
          <div className="flex items-center justify-center gap-3 mb-4 reveal-hidden">
            <div className="texas-line" />
            <span className="text-xs font-bold uppercase tracking-[0.14em] text-amber" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Common Questions
            </span>
            <div className="texas-line" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-navy leading-tight mb-4 reveal-hidden" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Frequently Asked Questions
          </h2>
          <p className="text-base text-gray-500 reveal-hidden reveal-delay-1">
            Honest answers about 5-ton Trane systems, sizing, and what to expect from installation.
          </p>
        </div>

        {/* FAQ items */}
        <div className="space-y-3 reveal-hidden reveal-delay-1">
          {faqs?.map((f, i) => (
            <div
              key={i}
              className={`border rounded-2xl overflow-hidden transition-all duration-300 ${
                openIndex === i ? 'border-navy/15 shadow-navy-sm' : 'border-gray-100'
              }`}
            >
              <button
                className="w-full flex items-start justify-between gap-4 px-6 py-5 text-left"
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                aria-expanded={openIndex === i}
              >
                <span className={`text-sm font-bold leading-snug transition-colors ${openIndex === i ? 'text-navy' : 'text-gray-700'}`} style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  {f?.q}
                </span>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 transition-all duration-300 ${openIndex === i ? 'bg-amber rotate-45' : 'bg-slate-light'}`}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={openIndex === i ? '#091628' : '#9CA3AF'} strokeWidth="2.5" strokeLinecap="round">
                    <path d="M12 5v14M5 12h14"/>
                  </svg>
                </div>
              </button>
              <div className={`faq-answer ${openIndex === i ? 'open' : ''}`}>
                <div className="px-6 pb-5">
                  <p className="text-sm text-gray-500 leading-relaxed">{f?.a}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}